// Shared pieces for the BigQuery insight sections (Dashboard, Barsys Analytics,
// Firebase Analytics). Data comes from GET /secureApi/analytics/bq-insights/*.

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardHeader, CardBody } from "../card/card.jsx";

export const DAY_OPTIONS = [7, 14, 30, 60, 90];

// Fixed colours (the theme colour is user-selectable and can be near-black).
export const COLORS = ["#3cd2a5", "#30beff", "#ff9f0c", "#e83293", "#8e7cf3", "#7e8c8d", "#f7cb73", "#4fc3a1"];

export function useBqInsights(section, days) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    axios
      .get(`${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureApi/analytics/bq-insights/${section}`, {
        params: { days },
        headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` },
      })
      .then((r) => !cancelled && setState({ data: r.data.data, loading: false, error: null }))
      .catch((e) => !cancelled && setState({ data: null, loading: false, error: e.response?.data?.message || e.message }));
    return () => {
      cancelled = true;
    };
  }, [section, days]);
  return state;
}

export const fmtInt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString());
export const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
// Round before formatting: src/index.js overrides Number.prototype.toLocaleString
// globally and ignores its options.
export const fmtMoney = (v) => (v === null || v === undefined ? "—" : `$${Math.round(Number(v)).toLocaleString()}`);

export function Change({ value }) {
  if (value === null || value === undefined) return <span className="text-inverse text-opacity-50">—</span>;
  const cls = value > 0 ? "text-success" : value < 0 ? "text-danger" : "text-inverse text-opacity-50";
  return <span className={cls}>{value > 0 ? "▲" : value < 0 ? "▼" : ""} {Math.abs(value)}%</span>;
}

export function DaysPicker({ days, setDays, disabled, options = DAY_OPTIONS }) {
  return (
    <div className="btn-group">
      {options.map((d) => (
        <button key={d} type="button" className={`btn btn-sm ${d === days ? "btn-theme" : "btn-outline-theme"}`} onClick={() => setDays(d)} disabled={disabled}>
          {d}d
        </button>
      ))}
    </div>
  );
}

export function Tile({ label, value, sub }) {
  return (
    <div className="col-6 col-md-4 col-xl-2 mb-3">
      <Card className="h-100">
        <CardBody>
          <div className="small text-inverse text-opacity-50 text-uppercase fw-bold mb-1">{label}</div>
          <div className="fs-4 fw-bold">{value}</div>
          {sub && <div className="small text-inverse text-opacity-50 mt-1">{sub}</div>}
        </CardBody>
      </Card>
    </div>
  );
}

// columns: [{ key, label, num?, fmt?, render?(row), width? }]
export function DataTable({ rows, columns, empty = "No data", maxHeight }) {
  if (!rows || !rows.length) return <div className="small text-inverse text-opacity-50 p-3">{empty}</div>;
  return (
    <div className="table-responsive" style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}>
      <table className="table table-sm mb-0">
        <thead>
          <tr>{columns.map((c) => <th key={c.key} className={c.num ? "text-end" : ""} style={{ whiteSpace: "nowrap" }}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} className={c.num ? "text-end text-nowrap" : "text-truncate"} style={c.num ? undefined : { maxWidth: c.width || 320 }} title={c.num ? undefined : String(r[c.key] ?? "")}>
                  {c.render ? c.render(r) : c.fmt ? c.fmt(r[c.key]) : c.num ? fmtInt(r[c.key]) : r[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Section({ title, subtitle, children, right, bodyClass }) {
  return (
    <Card className="mb-3">
      <CardHeader className="d-flex align-items-center flex-wrap fw-bold small">
        {title}
        {subtitle && <span className="fw-normal text-inverse text-opacity-50 ms-2">· {subtitle}</span>}
        {right && <div className="ms-auto">{right}</div>}
      </CardHeader>
      <CardBody className={bodyClass}>{children}</CardBody>
    </Card>
  );
}

export function StateBanner({ loading, error, hasData }) {
  if (error) return <div className="alert alert-danger">BigQuery: {error}</div>;
  if (loading && !hasData)
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-theme" />
      </div>
    );
  return null;
}

export function chartTheme() {
  const inverse = getComputedStyle(document.body).getPropertyValue("--bs-inverse").trim() || "#fff";
  const rgb = getComputedStyle(document.body).getPropertyValue("--bs-inverse-rgb").trim() || "255,255,255";
  const font = getComputedStyle(document.body).getPropertyValue("--bs-body-font-family").trim();
  return {
    chart: { toolbar: { show: false }, fontFamily: font, zoom: { enabled: false } },
    legend: { labels: { colors: inverse } },
    grid: { borderColor: `rgba(${rgb}, .15)` },
    tooltip: { theme: "dark" },
    dataLabels: { enabled: false },
    xaxisLabels: { style: { colors: inverse } },
    yaxisLabels: { style: { colors: inverse } },
  };
}
